import { twMerge } from 'tailwind-merge';
import { isInDateRange, isSameDate } from '@witmetrics/utils/time';
import { theme } from '@/utils/theme';
import { CALENDAR_WIDTH, EDGE_DAY_STYLES, PANEL_INSET } from './constants';

export const styles = {
  startDay: {
    '&': {
      ...EDGE_DAY_STYLES,
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
      marginRight: '0px',
    },
  },
  endDay: {
    '&': {
      ...EDGE_DAY_STYLES,
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
      marginLeft: '0px',
    },
  },
  sameDay: {
    '&': {
      ...EDGE_DAY_STYLES,
      // Default is 36 with a 2px horizontal margin
      width: '36px',
      marginLeft: '2px',
      marginRight: '2px',
    },
  },
  dayInRange: {
    '&:not(.Mui-selected)': {
      backgroundColor: theme.palette['orange-3'].main,
      color: theme.palette['white'].main,
      borderRadius: '0',
      marginLeft: 0,
      marginRight: 0,
      width: '40px', // Default is 36 with a 2px horizontal margin
    },
  },
  calendar: {
    '&.MuiDateCalendar-root': {
      width: `${CALENDAR_WIDTH}px`,
    },
    '& .MuiDayCalendar-header, & .MuiDayCalendar-monthContainer': {
      backgroundColor: 'rgba(120, 110, 100, 0.08)',
    },
    '& .MuiDayCalendar-header': {
      margin: `0 ${PANEL_INSET}px`,
      paddingTop: '4px',
      borderRadius: '14px 14px 0 0',
    },
    // Inset the transition wrapper, not the month, so the sliding clones keep the same width
    '& .MuiDayCalendar-slideTransition': {
      margin: `0 ${PANEL_INSET}px`,
    },
    '& .MuiDayCalendar-monthContainer': {
      paddingBottom: '8px',
      borderRadius: '0 0 14px 14px',
    },
    '& .MuiPickersDay-root.Mui-selected, .start-day, .end-day': {
      fontWeight: '700',
    },
    // MUI's own focus and hover rules outrank the plain selected rule
    '& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected:hover, & .MuiPickersYear-yearButton.Mui-selected:focus':
      {
        backgroundColor: theme.palette['orange-3'].main,
      },
    '& .MuiPickersDay-today.range-day:not(.Mui-selected)': {
      border: `1px solid ${theme.palette['orange-3'].main}`,
    },
    '& .MuiPickersDay-today:not(.Mui-selected):not(.range-day)': {
      border: `1px solid ${theme.palette['grey-2'].main}`,
      backgroundColor: theme.palette['grey-2'].main,
    },
    '& .MuiPickersDay-root:not(.range-day):hover': {
      backgroundColor: theme.palette['grey-2'].main,
    },
    '& .MuiPickersDay-root:focus.Mui-selected, & .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover':
      {
        backgroundColor: theme.palette['orange-3'].main,
      },
    '& .MuiDayCalendar-weekContainer .MuiPickersDay-root.range-day:last-of-type:not(.end-day):not(.same-day)':
      {
        borderTopRightRadius: theme.shape.rounded.lg,
        borderBottomRightRadius: theme.shape.rounded.lg,
      },
    '& .MuiDayCalendar-weekContainer .MuiPickersDay-root.range-day:first-of-type:not(.start-day):not(.same-day)':
      {
        borderTopLeftRadius: theme.shape.rounded.lg,
        borderBottomLeftRadius: theme.shape.rounded.lg,
      },
  },
};

function checkDay(day: Date, startDate: string, endDate: string) {
  const isStartDay = isSameDate(day.toISOString(), startDate);
  const isEndDay = isSameDate(day.toISOString(), endDate);
  return {
    isStartDay,
    isEndDay,
    isSameDay: isStartDay && isEndDay,
    isInRange: isInDateRange(day.toISOString(), [startDate, endDate]),
  };
}

export function getDayClassName(
  className: string | undefined,
  day: Date,
  startDate: string,
  endDate: string
) {
  const { isStartDay, isSameDay, isEndDay, isInRange } = checkDay(
    day,
    startDate,
    endDate
  );
  let dayClasses = '';
  if (isSameDay) dayClasses = 'range-day same-day';
  else if (isStartDay) dayClasses = 'range-day start-day';
  else if (isEndDay) dayClasses = 'range-day end-day';
  else if (isInRange) dayClasses = 'range-day';
  return twMerge(className, dayClasses);
}

export function getDayStyling(day: Date, startDate: string, endDate: string) {
  const { isStartDay, isSameDay, isEndDay, isInRange } = checkDay(
    day,
    startDate,
    endDate
  );
  if (isSameDay) return styles.sameDay;
  else if (isStartDay) return styles.startDay;
  else if (isEndDay) return styles.endDay;
  else if (isInRange) return styles.dayInRange;
  return {};
}
